/**
 * Telegram-style press-and-hold voice recorder hook.
 *
 * Wraps the bridge's microphone.* calls + ms-grained timer into a single
 * state machine the chat-input row can drive directly. State transitions:
 *
 *   initializing → idle → recording → processing → idle / error
 *
 * Call order:
 *   - `holdStart()` on pointerdown → enter `recording` (or `error` if init/start failed)
 *   - `holdEnd()`  on pointerup    → enter `processing`, then `idle` after the audio
 *                                     blob is handed to `onComplete`
 *   - `holdCancel()` on pointercancel / slide-away / cancel button →
 *                                     stop the native recorder and discard the result
 *
 * Edge cases preserved from the original modal implementation:
 *   - Releasing the button while `startRecording()` is still in flight queues
 *     a deferred stop so we don't record after the user has let go.
 *   - Empty / too-short / permission-denied recordings map to readable errors
 *     instead of a silent failure.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useBridge } from './useBridge';
import type { Attachment } from '../types';

export type VoiceRecorderState =
  | 'initializing'
  | 'idle'
  | 'recording'
  | 'processing'
  | 'error';

interface UseHoldVoiceRecorderOptions {
  onComplete: (attachment: Attachment) => void;
}

interface UseHoldVoiceRecorderReturn {
  state: VoiceRecorderState;
  /** Seconds since `holdStart()` — updates ~10× per second while recording. */
  duration: number;
  errorMsg: string | null;
  /** Imperative API: wire to pointer events on the mic button. */
  holdStart: () => void;
  holdEnd: () => void;
  holdCancel: () => void;
  /** Clear an error and return to `idle` so the user can try again. */
  dismissError: () => void;
}

export function useHoldVoiceRecorder({
  onComplete,
}: UseHoldVoiceRecorderOptions): UseHoldVoiceRecorderReturn {
  const {
    initializeAudioRecorder,
    isAudioReady,
    startRecording,
    stopRecording,
    audioResultToAttachment,
  } = useBridge();

  const [state, setState] = useState<VoiceRecorderState>('initializing');
  const [duration, setDuration] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  /** Native recorder is actively capturing. */
  const isRecordingRef = useRef(false);
  /** `startRecording()` is in flight — native recorder not yet up. */
  const isStartingRef = useRef(false);
  /** User released before start finished; stop immediately once start completes. */
  const releasePendingRef = useRef(false);
  /** If true, the in-flight finalize was triggered by a cancel — discard audio. */
  const cancelRequestedRef = useRef(false);

  // Initialize on mount (no-op if the bridge already reports ready).
  useEffect(() => {
    let cancelled = false;
    if (isAudioReady) {
      setState('idle');
      return;
    }
    (async () => {
      try {
        const ok = await initializeAudioRecorder();
        if (cancelled) return;
        if (ok) {
          setState('idle');
        } else {
          setState('error');
          setErrorMsg('Failed to initialize microphone. Check permissions.');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('useHoldVoiceRecorder init error:', err);
        setState('error');
        setErrorMsg('Unexpected error initializing microphone');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initializeAudioRecorder, isAudioReady]);

  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    setDuration(0);
    timerRef.current = setInterval(() => {
      setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 100);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Unmount cleanup. If we're still recording when the consumer unmounts,
  // stop the native recorder so the OS mic indicator doesn't linger.
  useEffect(() => {
    return () => {
      stopTimer();
      if (isRecordingRef.current) {
        void stopRecording('base64').catch(() => {
          /* best-effort cleanup */
        });
        isRecordingRef.current = false;
      }
    };
  }, [stopTimer, stopRecording]);

  const finalize = useCallback(
    async (intent: 'send' | 'cancel') => {
      isRecordingRef.current = false;
      isStartingRef.current = false;
      setState('processing');
      stopTimer();

      const stopped = await stopRecording('base64');

      if (intent === 'cancel') {
        // User discarded mid-recording. Drop the audio regardless of result.
        setState('idle');
        setDuration(0);
        return;
      }

      if (stopped.ok) {
        const attachment = audioResultToAttachment(stopped.result);
        if (attachment) {
          onComplete(attachment);
          setState('idle');
          setDuration(0);
          return;
        }
        setErrorMsg('Recording succeeded but could not be prepared for upload.');
        setState('error');
        return;
      }

      // Stop failed — map reason to a readable message (same copy as the
      // legacy modal so behaviour is unchanged where it matters).
      let message: string;
      if (stopped.reason === 'empty-recording') {
        const status = stopped.permissionStatus;
        if (status && status !== 'granted') {
          message =
            'Microphone permission is off. Open Settings → DG SuperApp → ' +
            'Microphone and turn it on, then try again.';
        } else {
          message =
            'That recording was too short. Hold the mic while you speak, ' +
            'then release to send.';
        }
      } else if (stopped.reason === 'stop-failed') {
        message = stopped.detail
          ? `Recorder could not stop: ${stopped.detail}`
          : 'Recorder could not stop. Try again.';
      } else if (stopped.reason === 'no-data') {
        message = 'Recorder returned no data. Try again.';
      } else {
        message = stopped.detail
          ? `Unexpected error: ${stopped.detail}`
          : 'Failed to process recording.';
      }

      setErrorMsg(message);
      setState('error');
    },
    [stopRecording, audioResultToAttachment, stopTimer, onComplete],
  );

  const holdStart = useCallback(() => {
    if (state !== 'idle' || isRecordingRef.current || isStartingRef.current) return;

    setErrorMsg(null);
    cancelRequestedRef.current = false;
    isStartingRef.current = true;
    setState('processing');

    (async () => {
      try {
        // AAC (not opus): legacy modal proved opus produces silent fake-success
        // on this bridge build. Server-side ffmpeg transcodes AAC → OGG+Opus
        // for the Telegram voice bubble, so AAC here costs nothing.
        const result = await startRecording({
          encoder: 'aac',
          sampleRate: 44100,
          bitRate: 128000,
        });

        if (!result.ok) {
          isStartingRef.current = false;
          releasePendingRef.current = false;
          const message =
            result.reason === 'init-failed'
              ? 'Could not initialise the recorder. Close and re-open the app, then try again.'
              : result.reason === 'host-rejected'
                ? result.detail
                  ? `Recorder: ${result.detail}`
                  : 'Recorder rejected the request. Check microphone permissions.'
                : `Unexpected error${result.detail ? `: ${result.detail}` : ''}. Try again.`;
          setErrorMsg(message);
          setState('error');
          return;
        }

        // User released during async start — stop immediately so we don't
        // record after the finger lifted.
        if (releasePendingRef.current) {
          releasePendingRef.current = false;
          await finalize(cancelRequestedRef.current ? 'cancel' : 'send');
          return;
        }

        isStartingRef.current = false;
        isRecordingRef.current = true;
        setState('recording');
        startTimer();
      } catch (err) {
        console.error('useHoldVoiceRecorder start error:', err);
        isStartingRef.current = false;
        releasePendingRef.current = false;
        setErrorMsg('Unexpected error during recording');
        setState('error');
      }
    })();
  }, [state, startRecording, startTimer, finalize]);

  const holdEnd = useCallback(() => {
    if (isStartingRef.current) {
      // Start still in flight — queue the stop for when it completes.
      releasePendingRef.current = true;
      cancelRequestedRef.current = false;
      return;
    }
    if (!isRecordingRef.current) return;
    void finalize('send');
  }, [finalize]);

  const holdCancel = useCallback(() => {
    if (isStartingRef.current) {
      // Start still in flight — queue cancel for when it completes.
      releasePendingRef.current = true;
      cancelRequestedRef.current = true;
      return;
    }
    if (!isRecordingRef.current) return;
    void finalize('cancel');
  }, [finalize]);

  const dismissError = useCallback(() => {
    setErrorMsg(null);
    setState('idle');
  }, []);

  return {
    state,
    duration,
    errorMsg,
    holdStart,
    holdEnd,
    holdCancel,
    dismissError,
  };
}
